import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import type { PageProps } from "@/types/inertia"

type ClientOption = {
  readonly id: number
  readonly slug: string
  readonly display_name: string
  readonly color_hex: string
}

type AccountSummary = {
  readonly id: number
  readonly name: string
  readonly domain: string
  readonly website: string
  readonly industry: string
  readonly employee_range: string
  readonly icp_fit: string
  readonly contacts_count: number
  readonly opportunities_count: number
  readonly client: ClientOption
}

type AccountsIndexProps = {
  readonly accounts: readonly AccountSummary[]
  readonly clients: readonly ClientOption[]
  readonly filters: { readonly q: string; readonly client_id: string | null }
}

export default function Accounts({ accounts, filters }: AccountsIndexProps) {
  const { props } = usePage<PageProps<AccountsIndexProps>>()
  const [q, setQ] = React.useState(filters.q)
  const description = "Browse and search CRM accounts with contact and opportunity counts."

  const search = (value: string) => {
    setQ(value)
    router.get(
      "/crm/accounts",
      { q: value, client_id: filters.client_id ?? undefined },
      { preserveState: true, preserveScroll: true, replace: true },
    )
  }

  return (
    <>
      <Head title="Accounts">
        <meta name="description" content={description} />
        <meta property="og:title" content="Accounts" />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <div className="flex flex-col gap-6">
          <header>
            <h1>Accounts</h1>
            <p className="mt-2">Every company domain with linked contacts, opportunities, and replies.</p>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <div className="max-w-sm">
            <label htmlFor="account-search" className="sr-only">
              Search accounts
            </label>
            <Input
              id="account-search"
              placeholder="Search by name or domain…"
              value={q}
              onChange={(event) => search(event.target.value)}
            />
          </div>

          {accounts.length > 0 ? (
            <section className="overflow-hidden rounded-lg border border-hairline bg-page">
              <div className="divide-y divide-hairline">
                {accounts.map((account) => (
                  <Link
                    key={account.id}
                    href={`/crm/accounts/${account.id}`}
                    className="grid gap-2 px-4 py-4 no-underline hover:bg-surface sm:grid-cols-[1fr_1fr_auto] sm:items-center sm:gap-4"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 rounded-full border border-hairline shrink-0"
                          style={{ backgroundColor: account.client.color_hex }}
                        />
                        <div className="truncate text-ink-display">
                          {account.name || account.domain}
                        </div>
                      </div>
                      <div className="mt-1 truncate text-sm text-ink-muted">
                        {account.domain}
                      </div>
                      {account.industry && (
                        <div className="mt-0.5 text-sm text-ink-muted">
                          {account.industry}{account.employee_range ? ` · ${account.employee_range}` : ""}
                        </div>
                      )}
                    </div>
                    <div className="truncate text-sm text-ink-muted">
                      {account.icp_fit && account.icp_fit !== "unknown" && (
                        <Badge tone="muted">{account.icp_fit}</Badge>
                      )}
                    </div>
                    <div className="flex gap-2 sm:justify-end">
                      <Badge tone="muted">{account.contacts_count} contacts</Badge>
                      <Badge tone={account.opportunities_count > 0 ? "accent" : "muted"}>
                        {account.opportunities_count} opportunities
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ) : (
            <section className="rounded-lg border border-dashed border-hairline bg-surface px-6 py-12 text-center">
              <h2>No accounts found</h2>
              <p className="mt-2">
                {filters.q ? "Try a different search term." : "Accounts appear here once contacts are linked to company domains."}
              </p>
            </section>
          )}
        </div>
      </AppShell>
    </>
  )
}
